import React, { useContext, useState } from 'react'; // Import useState
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import AuthContext, { AuthProvider } from '../context/AuthContext';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import AppBar from '@mui/material/AppBar'; // Import AppBar
import Toolbar from '@mui/material/Toolbar'; // Import Toolbar
import IconButton from '@mui/material/IconButton'; // Import IconButton
import Menu from '@mui/material/Menu'; // Import Menu
import MenuItem from '@mui/material/MenuItem'; // Import MenuItem
import Button from '@mui/material/Button';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsightsIcon from '@mui/icons-material/Insights';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountCircle from '@mui/icons-material/AccountCircle'; // Icon for user menu
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
// import Report from '@mui/icons-material/Report'; // Icon for reports
import { PendingActions } from '@mui/icons-material';

// Custom styled Paper for dashboard items
const DashboardItem = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  borderRadius: theme.shape.borderRadius * 1.5,
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[8],
  },
}));

const Selection = () => {
  // let { logOutUser } = useContext(AuthContext)
  let { user, logOutUser } = useContext(AuthContext); // Destructure logoutUser if available
  let navigate = useNavigate(); // Initialize useNavigate
  const [anchorEl, setAnchorEl] = useState(null); // State for the user menu anchor

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // const handleLogout = () => {
  //   handleCloseMenu();
  //   if (logoutUser) {
  //     logoutUser(); // Call the logout function from AuthContext if available
  //   } else {
  //     // Fallback if logoutUser is not provided by context
  //     console.log('Logging out...');
  //     // Clear session/local storage manually if no context function
  //     localStorage.removeItem('authToken'); // Example: remove a token
  //     sessionStorage.removeItem('userPhone'); // Example: remove other session data
  //   }
  //   navigate('/login'); // Redirect to login page
  // };

  const handleGoToDashboard = () => {
    navigate('/'); // Navigate to the dashboard route
    handleCloseMenu();
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 0, // Set py to 0 to prevent padding above the AppBar
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
      }}
    >
      {/* Top Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#2E7D32' }}> {/* Green AppBar */}
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Volunteer Dashboard
          </Typography>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            onClick={handleGoToDashboard}
            sx={{ display: { xs: 'none', sm: 'flex' } }} // Hide on extra-small screens
          >
            Dashboard
          </Button>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleGoToDashboard} sx={{ display: { sm: 'none' } }}>
              <DashboardIcon sx={{ mr: 1 }} /> Dashboard
            </MenuItem>
            <MenuItem onClick={logOutUser}>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ pt: 6, pb: 6 }}> {/* Added top and bottom padding */}
        {/* Personalized Greeting */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 5, justifyContent: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mr: 2, fontSize: '2rem' }}>
            {user && user.username ? user.username.charAt(0).toUpperCase() : ''} {/* Get first letter of username */}
          </Avatar>
          <Typography variant="h5" component="h1" // Adjusted variant for better sizing
            sx={{
              color: 'primary.dark',
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
              textAlign: { xs: 'center', sm: 'left' } // Center text on small screens if avatar wraps
            }}
          >
            Hi {user && user.first_name ? user.first_name : 'Volunteer'}, your dashboard awaits!
          </Typography>
        </Box>

        {/* Dashboard Items */}
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">

          {/* Add Innovator */}
          <Grid item xs={12} sm={6} md={4}>
            <ButtonBase sx={{ width: '100%', height: '100%' }} onClick={() => { window.location = 'innovator-phone' }}>
              <DashboardItem elevation={3}>
                <PersonAddIcon sx={{ fontSize: 55, color: 'info.main', mb: 1 }} />
                <Typography variant="h6" component="div" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                  Add Innovator
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  Register a new grassroots innovator.
                </Typography>
              </DashboardItem>
            </ButtonBase>
          </Grid>

          {/* My Innovators */}
          <Grid item xs={12} sm={6} md={4}>
            <ButtonBase sx={{ width: '100%', height: '100%' }} onClick={() => { window.location = 'check-phone-for-media-upload' }}> {/* Updated onClick target */}
              <DashboardItem elevation={3}>
                <AddPhotoAlternateIcon sx={{ fontSize: 55, color: 'secondary.main', mb: 1 }} /> {/* New icon */}
                <Typography variant="h6" component="div" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                  Add Innovator Photos & Videos
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  Upload media for existing innovators.
                </Typography>
              </DashboardItem>
            </ButtonBase>
          </Grid>

          {/* My Activities */}
          <Grid item xs={12} sm={6} md={4}>
            <ButtonBase sx={{ width: '100%', height: '100%' }} onClick={() => { window.location = 'select-scouting-report-type/' }}>
              <DashboardItem elevation={3}>
                <PendingActions sx={{ fontSize: 55, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" component="div" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                  Scouting Report & Images
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  Log your scouting experience & learnings.
                </Typography>
              </DashboardItem>
            </ButtonBase>
          </Grid>

          {/* My Submissions */}
          <Grid item xs={12} sm={6} md={4}>
            <ButtonBase sx={{ width: '100%', height: '100%' }} onClick={() => { window.location = 'my-submissions' }}>
              <DashboardItem elevation={3}>
                <LightbulbIcon sx={{ fontSize: 55, color: 'error.main', mb: 1 }} />
                <Typography variant="h6" component="div" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                  My Submissions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  Track the status of innovations you've submitted.
                </Typography>
              </DashboardItem>
            </ButtonBase>
          </Grid>

          {/* My Reports / Personal Insights */}
          <Grid item xs={12} sm={6} md={4}>
            <ButtonBase sx={{ width: '100%', height: '100%' }} onClick={() => { window.location = 'my-reports' }}>
              <DashboardItem elevation={3}>
                <InsightsIcon sx={{ fontSize: 55, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" component="div" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                  My Impact
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  View personal statistics and impact on scouting.
                </Typography>
              </DashboardItem>
            </ButtonBase>
          </Grid>

          {/* Optional: Community/Resources - if applicable for volunteers */}
          <Grid item xs={12} sm={6} md={4}>
            <ButtonBase sx={{ width: '100%', height: '100%' }} onClick={() => { window.location = 'resources' }}>
              <DashboardItem elevation={3}>
                <AssignmentIcon sx={{ fontSize: 55, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" component="div" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                  Resources
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  Access guides, forms, and training materials.
                </Typography>
              </DashboardItem>
            </ButtonBase>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default Selection;